import React, { useState, useMemo, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import {
    SideBarStates,
    useSidebarDispatch,
    useSidebarState,
} from '../../../context/SidebarContext';
import { BasePanelComponent } from './CommonComponents';
import { SearchIcon } from '@heroicons/react/outline';
import { useUserSession } from '@hooks/user/useDefaultSession';
import {
    Activity,
    useCalendarEventsQuery,
    useCalendarsQuery,
    useCourseActivitiesCalendarDataQuery,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useRouter } from 'next/router';
import {
    CalendarIconAssignmentSVG,
    CalendarIconQuizSVG,
} from '@components/common/svgs/CalendarActivityIconsSVG';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { useQueryClient } from 'react-query';
import { RoundCalendar } from '@components/v2Calendar/Calendar';
import { useCourseUnitsMenuDispatcher } from '@components/layout/leftNav/menus/units/CourseUnitsMenu';
import { hexToRgba } from '@utils/hexToRgba';
import { timeDetails } from '@utils/dates';
import { EventPreviewModal } from '@components/modals/calendar/EventPreviewModal';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { useEnrollHandler } from '@hooks/useEnrollmentsWithMapping';
import useIsMobile from '@hooks/useIsMobile';
import { AnimatedSearchField } from '@components/form/animated/AnimatedSearchField';
import { useDebounce } from '@hooks/useDebounce';
import { transformString } from '@utils/transformString';
import { SimpleDropdown } from '@components/common/dropdowns/SimpleDropdown';
import { DoubleArrowSVG } from '@components/common/svgs/DoubleArrowsSVG';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListBox } from '@components/base/forms/List/ListBox';

export function AgendaPanel() {
    const dispatcher = useCourseUnitsMenuDispatcher();
    const [nextWeekActivities, setNextWeekActivities] = useState([]);
    const [nextWeekEvents, setNextWeekEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [fetchEvents, setFetchEvents] = useState(false);
    const queryClient = useQueryClient();
    const router = useRouter();

    const dateFilters = useMemo(() => {
        return [
            {
                id: 'all',
                title: 'Show all',
            },
            {
                id: 'assignDate',
                title: 'Show by assign date',
            },
            {
                id: 'dueDate',
                title: 'Show by due date',
            },
        ];
    }, []);

    const [selectedDateFilter, setSelectedDateFilter] = useState(
        JSON.parse(localStorage.getItem('agendaDateFilter')) || dateFilters[0],
    );

    const debounceSearchKeyword = useDebounce(
        searchTerm.replace(/\s+/g, '').trim().toLowerCase(),
        500,
    );

    const isMobile = useIsMobile();

    const { toggle: sidebarToggle } = useSidebarDispatch();

    const { enrolls, loading: coursesLoading, isRefetching } = useEnrollHandler();
    const myCourses = useMemo(() => {
        return enrolls?.leadingCourses?.concat(enrolls?.takingCourses, enrolls?.observingCouses);
    }, [enrolls]);

    const { state, agendaDate } = useSidebarState();

    const [selectedDay, setSelectedDay] = useState(agendaDate);

    const [selectedMonth, setSelectedMonth] = useState(dayjs(selectedDay).month());
    const [selectedYear, setSelectedYear] = useState(dayjs(selectedDay).year());

    // const [nextYear, setNextYear] = useState(dayjs().year(dayjs(selectedDay).year()));

    const { toggle: modalToggle } = useUniversalModalDispatch();

    const [currentUser] = useUserSession();

    useEffect(() => {
        queryClient.invalidateQueries('useCourseActivitiesCalendarDataQuery');
    }, [currentUser?.id]);

    useEffect(() => {
        setSelectedDay(agendaDate);
        setSelectedMonth(dayjs(agendaDate).month());
    }, [agendaDate]);

    const firstDayofMonth = useMemo(() => {
        return dayjs()
            .month(selectedMonth)
            .year(selectedYear)
            .startOf('month')
            .format('YYYY-MM-DD');
    }, [selectedMonth, selectedYear]);

    const lastDayofMonth = useMemo(() => {
        return dayjs()
            .month(selectedMonth)
            .year(selectedYear)
            .endOf('month')
            .add(7, 'day')
            .format('YYYY-MM-DD');
    }, [selectedMonth, selectedYear]);

    const { data, isLoading, isFetching } = useCourseActivitiesCalendarDataQuery(
        getClient('calendar'),
        {
            startDate: firstDayofMonth,
            endDate: lastDayofMonth,
        },
        { refetchOnWindowFocus: false },
    );

    const {
        data: calendarsData,
        isLoading: calendarsLoading,
        isFetching: calendarsFetching,
    } = useCalendarsQuery(getClient('calendar'), undefined, {
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: 300000,
    });

    useEffect(() => {
        calendarsData?.calendars?.length > 0 ? setFetchEvents(true) : setFetchEvents(false);
    }, [calendarsData]);

    const {
        data: eventsData,
        isLoading: eventsLoading,
        isFetching: eventsFetching,
    } = useCalendarEventsQuery(
        getClient('calendar'),
        {
            calendarIds: calendarsData?.calendars[0]?.id || '',
            startDate: dayjs(firstDayofMonth),
            endDate: dayjs(lastDayofMonth),
        },
        {
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            refetchInterval: 300000,
            enabled: fetchEvents,
        },
    );

    const isTheSameMonth = dayjs(selectedDay).month() === selectedMonth;

    const daysWithEvents = useMemo(() => {
        let days = [];
        data?.courseActivities?.map((activity) => {
            const dueDate = dayjs(activity?.dueDate).format('YYYY-MM-DD');
            const assignDate = dayjs(activity?.assignDate).format('YYYY-MM-DD');
            const addDueDate =
                dayjs(dueDate).isAfter(dayjs(firstDayofMonth).subtract(1, 'day')) &&
                dayjs(dueDate).isBefore(dayjs(lastDayofMonth).add(1, 'day')) &&
                !days.includes(dueDate);
            const addAssignDate =
                dayjs(assignDate).isAfter(dayjs(firstDayofMonth).subtract(1, 'day')) &&
                dayjs(assignDate).isBefore(dayjs(lastDayofMonth).add(1, 'day')) &&
                !days.includes(assignDate);
            if (!!selectedDateFilter && selectedDateFilter?.id !== 'all') {
                // const filteredByDueDate = dueDate === day;
                // const filteredAssignDate = assignDate === day;
                if (selectedDateFilter?.id === 'dueDate') return addDueDate && days.push(dueDate);
                if (selectedDateFilter?.id === 'assignDate')
                    return addAssignDate && days.push(assignDate);
            }
            addDueDate && days.push(dueDate);
            addAssignDate && days.push(assignDate);
        });
        eventsData?.calendarEvents?.map((event) => {
            const date = dayjs(event?.date).format('YYYY-MM-DD');
            dayjs(date).isAfter(dayjs(firstDayofMonth).subtract(1, 'day')) &&
                dayjs(date).isBefore(dayjs(lastDayofMonth).add(1, 'day')) &&
                !days.includes(date) &&
                days.push(date);
        });
        return days;
    }, [
        data?.courseActivities,
        eventsData?.calendarEvents,
        firstDayofMonth,
        lastDayofMonth,
        selectedDateFilter,
    ]);

    useEffect(() => {
        if (state === SideBarStates.Agenda) {
            queryClient.invalidateQueries('CourseActivitiesCalendarData', {
                refetchInactive: true,
            });
        }
    }, [state]);

    useEffect(() => {
        if (isTheSameMonth) {
            const activitites = data?.courseActivities?.filter((activity) => {
                if (
                    !!debounceSearchKeyword &&
                    !transformString(activity?.item?.title).includes(debounceSearchKeyword)
                )
                    return;
                const dueDateInRange = dayjs(activity?.dueDate).isAfter(selectedDay);
                return dueDateInRange;
            });
            const events = eventsData?.calendarEvents?.filter((event) => {
                if (
                    !!debounceSearchKeyword &&
                    !transformString(event?.title).includes(debounceSearchKeyword)
                )
                    return;
                const eventDateInRange = dateInRange({ date: event?.date, selectedDay });
                return eventDateInRange;
            });
            setNextWeekActivities(activitites);
            setNextWeekEvents(events);
        }
    }, [
        data?.courseActivities,
        selectedDay,
        isTheSameMonth,
        eventsData?.calendarEvents,
        debounceSearchKeyword,
    ]);

    const { daysToShow } = useMemo(() => {
        let daysToShow = [];
        let day = 0;
        while (day < 7) {
            daysToShow.push(dayjs(selectedDay).add(day, 'day').format('YYYY-MM-DD'));
            day++;
        }
        return { daysToShow };
    }, [selectedDay]);

    const { dailyActivitiesBreakdown } = useMemo(() => {
        let dailyActivitiesBreakdown = [];
        daysToShow?.map((day) => {
            const dayActivities = nextWeekActivities?.filter((activity) => {
                const dueDate = dayjs(activity?.dueDate).format('YYYY-MM-DD');
                const assignDate = dayjs(activity?.assignDate).format('YYYY-MM-DD');
                if (!!selectedDateFilter && selectedDateFilter?.id !== 'all') {
                    const filteredByDueDate = dueDate === day;
                    const filteredAssignDate = assignDate === day;
                    if (selectedDateFilter?.id === 'dueDate') return filteredByDueDate;
                    if (selectedDateFilter?.id === 'assignDate') return filteredAssignDate;
                }
                return (
                    dueDate === day ||
                    assignDate === day ||
                    (dayjs(day).isAfter(assignDate) &&
                        dayjs(day).isBefore(dueDate) &&
                        dayjs().isAfter(assignDate) &&
                        dayjs().isBefore(dueDate))
                );
            });
            let courses = [];
            dayActivities?.map((activity) => {
                const courseId = activity?.unit?.course?.id;
                const course = courses.find((c) => c.courseId === courseId);
                !course
                    ? courses.push({
                          courseActivities: [activity],
                          courseColor: activity?.unit?.course?.iconColor,
                          courseId: courseId,
                      })
                    : course.courseActivities.push(activity);
            });
            const sortedCourseActivities = courses?.map((course) => {
                return {
                    ...course,
                    courseActivities: course.courseActivities.sort((a: any, b: any) =>
                        dayjs(a?.dueDate)
                            .format('hh:mm a')
                            .localeCompare(dayjs(b?.dueDate).format('hh:mm a')),
                    ),
                };
            });
            dailyActivitiesBreakdown[day] = sortedCourseActivities;
        });
        return { dailyActivitiesBreakdown };
    }, [nextWeekActivities, daysToShow, selectedDateFilter]);

    const { dailyEventsBreakdown } = useMemo(() => {
        let dailyEventsBreakdown = [];
        daysToShow?.map((day) => {
            const dayEvents = nextWeekEvents?.filter((event) => {
                return dayjs(event?.date).format('YYYY-MM-DD') === day;
            });
            dailyEventsBreakdown[day] = dayEvents?.sort((a: any, b: any) =>
                a?.timeStart?.localeCompare(b?.timeStart),
            );
        });
        return { dailyEventsBreakdown };
    }, [nextWeekEvents, daysToShow]);

    const initialDate = dayjs();

    const dataWasLoaded = useMemo(() => {
        return (
            !!nextWeekActivities &&
            !!nextWeekActivities?.length &&
            !!nextWeekEvents?.length &&
            !!nextWeekEvents
        );
    }, [nextWeekActivities, nextWeekEvents]);

    const dataIsLoading = useMemo(() => {
        return isLoading || isFetching || eventsFetching || eventsLoading || calendarsLoading;
    }, [isLoading, isFetching, eventsFetching, eventsLoading, calendarsLoading]);

    return (
        <BasePanelComponent openState={SideBarStates.Agenda}>
            <BasePanelComponent.Header
                title="Agenda"
                content={
                    <div className="flex justify-center items-center">
                        <AnimatedSearchField
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }}
                            // onClose={() => {}}
                            onClose={() => {
                                setSearchTerm('');
                            }}
                            size="small"
                            placeholder="Search"
                        />
                        {/* <FontAwesomeIcon icon={faPrint} size="lg" className="mr-2 text-gray-400" />
                        <PlusIcon className="h-8 w-8 text-teal-600" /> */}
                    </div>
                }></BasePanelComponent.Header>
            <BasePanelComponent.Body>
                <div className="flex flex-col items-center mt-10">
                    <RoundCalendar
                        setSelectedDay={setSelectedDay}
                        setSelectedMonth={setSelectedMonth}
                        setSelectedYear={setSelectedYear}
                        daysWithEvents={daysWithEvents}
                        initialDate={initialDate}
                        selectedDay={selectedDay}
                        showLoadingState={dataIsLoading}
                        holidays={[]}
                    />
                </div>
                {isLoading && !dataWasLoaded ? (
                    <div className="flex justify-center items-center mt-10">
                        <SimpleSpinner className="text-gray-300" />
                    </div>
                ) : (
                    <div className="bg-white px-3 pb-5 relative mt-10">
                        <div className="absolute -top-1 right-1 flex w-1/3">
                            <SimpleDropdown
                                id="dateFilter"
                                size="xsmall"
                                label=""
                                value={selectedDateFilter?.title}
                                className="border-none h-[2] p-0"
                                onChange={(select) => {
                                    setSelectedDateFilter(select);
                                    localStorage.setItem(
                                        'agendaDateFilter',
                                        JSON.stringify(select),
                                    );
                                }}
                                getValue={(select) => {
                                    return <p>{select}</p>;
                                }}
                                placeholder="Select group"
                                IconComponent={
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        size="xs"
                                        className="text-gray-500"
                                    />
                                }
                                hasError={false}>
                                {dateFilters?.map((f) => {
                                    return (
                                        <ListBox.Option
                                            id={f?.id}
                                            key={f?.id}
                                            value={{ id: f?.id, title: f?.title }}>
                                            {({}) => (
                                                <div className={`cursor-pointer text-sm`}>
                                                    {f?.title}
                                                </div>
                                            )}
                                        </ListBox.Option>
                                    );
                                })}
                            </SimpleDropdown>
                        </div>
                        {daysToShow?.length > 0 &&
                            daysToShow?.map((day) => {
                                return (
                                    <React.Fragment key={`dayBlock_${dayjs(day).format('MMMM_D')}`}>
                                        <div
                                            key={dayjs(day).format('MMMM D')}
                                            className="text-md leading-5 mt-5 font-medium border-b py-1 mb-3 border-gray-200">
                                            {dayjs(day).format('MMMM D')}
                                        </div>
                                        {dailyActivitiesBreakdown[day]?.length === 0 &&
                                        dailyEventsBreakdown[day]?.length === 0 ? (
                                            <div className="text-sm leading-4 text-gray-500 text-center">
                                                No events or activities on this day
                                            </div>
                                        ) : (
                                            <>
                                                {dailyEventsBreakdown[day]?.map((event) => {
                                                    const bgColor = event?.course?.iconColor || '';
                                                    return (
                                                        <div
                                                            key={event?.id}
                                                            className="mt-1 cursor-pointer rounded text-white py-1 px-2 w-full truncate"
                                                            style={{
                                                                backgroundColor:
                                                                    bgColor || '#01AAB9',
                                                            }}
                                                            onClick={() =>
                                                                modalToggle(
                                                                    true,
                                                                    <EventPreviewModal
                                                                        event={event}
                                                                        courseName={
                                                                            event?.course?.title
                                                                        }
                                                                        color={bgColor || '#01AAB9'}
                                                                        courseId={event.course.id}
                                                                        coursesList={myCourses.filter(
                                                                            (course) =>
                                                                                course?.role.permissions?.includes(
                                                                                    'manage_course_events',
                                                                                ),
                                                                        )}
                                                                        setEventsReload={() => {}}
                                                                        setFirstLoad={() => {}}
                                                                    />,
                                                                )
                                                            }>
                                                            {event?.title}
                                                        </div>
                                                    );
                                                })}

                                                {dailyActivitiesBreakdown[day]?.map((course) => {
                                                    return course?.courseActivities?.map(
                                                        (activity) => {
                                                            const bgColor =
                                                                course.courseColor || '';

                                                            const { hours, minutes, dayTime } =
                                                                timeDetails(activity?.dueDate);

                                                            const showDueTime =
                                                                activity?.dueTimeSignificant &&
                                                                day ===
                                                                    dayjs(activity?.dueDate).format(
                                                                        'YYYY-MM-DD',
                                                                    );

                                                            return (
                                                                <div
                                                                    key={activity?.id}
                                                                    className={`mt-2 px-3 py-1 rounded cursor-pointer flex items-center text-sm leading-4 justify-between w-full`}
                                                                    style={{
                                                                        backgroundColor: hexToRgba({
                                                                            hex: bgColor,
                                                                            opacity: 0.1,
                                                                        }),
                                                                    }}
                                                                    onClick={() => {
                                                                        if (isMobile) {
                                                                            sidebarToggle(
                                                                                SideBarStates.Closed,
                                                                            );
                                                                        }
                                                                        dispatcher.setUnit(
                                                                            activity?.unit?.id,
                                                                        );
                                                                        router.push(
                                                                            `/courses/${course.courseId}/units/${activity?.unit?.id}/activities/${activity?.id}`,
                                                                        );
                                                                    }}>
                                                                    <div className="flex w-[80%]">
                                                                        {activity?.item?.type ===
                                                                        'assignment' ? (
                                                                            <CalendarIconAssignmentSVG className="mr-2" />
                                                                        ) : (
                                                                            <CalendarIconQuizSVG className="mr-2" />
                                                                        )}
                                                                        <div className="truncate w-full text-ellipsis">
                                                                            {activity?.item?.title}
                                                                        </div>
                                                                    </div>
                                                                    {showDueTime && (
                                                                        <div className="ml-2 text-gray-500">
                                                                            {hours}:{minutes}
                                                                            {dayTime}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                    );
                                                })}
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                    </div>
                )}
            </BasePanelComponent.Body>
        </BasePanelComponent>
    );
}

const dateInRange = ({ date, selectedDay }: { date: string; selectedDay: dayjs.Dayjs }) => {
    return (
        // dayjs(date).isBefore(selectedDay.add(6, 'day')) &&
        // dayjs(date).isAfter(selectedDay.subtract(1, 'day'))
        dayjs(date).isBefore(selectedDay.add(6, 'day')) &&
        dayjs(date).isAfter(selectedDay.subtract(1, 'day'))
    );
};

const shouldShowActivity = ({
    date,
    dailyActivitiesBreakdown,
    activity,
}: {
    date: string;
    dailyActivitiesBreakdown: any;
    activity: Activity;
}) => {
    if (!dailyActivitiesBreakdown[date]) dailyActivitiesBreakdown[date] = [activity];
    else {
        if (dailyActivitiesBreakdown[date].includes(activity)) return;
        dailyActivitiesBreakdown[date].push(activity);
    }
};
